import { useGetBuckets } from '@shadcn/hooks/use-storage';
import { formatDate } from 'date-fns';
import { Alert } from '~/components/ui/Alert';
import { LabelBadge } from '~/components/ui/LabelBadge';
import { Skeleton } from '~/components/ui/Skeleton';
import * as Tooltip from '~/components/ui/Tooltip';

export function StorageManager() {
  const { data: buckets, isLoading, isError } = useGetBuckets();
  const labelBadgeClassName = 'w-min ml-auto';

  return (
    <div className="pt-12">
      <h1 className="text-base lg:text-xl font-semibold">File Storage</h1>
      <p className="hidden lg:block text-sm lg:text-base text-bolt-ds-textSecondary mt-2 mb-6">
        View and manage the files stored in your app.
      </p>

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {isError && (
        <Alert type="danger" title="Error loading buckets">
          There was a problem loading your storage buckets.
        </Alert>
      )}

      {buckets && buckets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {buckets.map((bucket: any) => (
            <div
              key={bucket.id}
              className="bg-bolt-ds-surface rounded-md border border-bolt-ds-borderOutline flex items-start justify-between py-3 px-4"
            >
              <div className="flex gap-3 items-start">
                <div className="i-heroicons:folder-open size-5 text-bolt-ds-textTertiary mt-1" />
                <div className="flex flex-col gap-0.5">
                  <h2 className="font-semibold">{bucket.name}</h2>
                  <p className="text-xs text-bolt-ds-textSecondary">
                    Updated {formatDate(new Date(bucket.updated_at), 'MMMM d, yyyy')}
                  </p>
                </div>
              </div>
              {bucket.public ? (
                <Tooltip.Provider key={bucket.id}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <LabelBadge variant="primary" className={labelBadgeClassName}>
                        Public
                      </LabelBadge>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content>
                        <p>These files can be accessed by anyone if the link is shared.</p>
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              ) : (
                <LabelBadge variant="secondary" className={labelBadgeClassName}>
                  Private
                </LabelBadge>
              )}
            </div>
          ))}
        </div>
      ) : buckets && buckets.length === 0 ? (
        <div className="text-bolt-ds-textTertiary">You don't have any files stored yet.</div>
      ) : null}
    </div>
  );
}
